import { IBBox } from '@antv/g6';

import {
  Directionality,
  Edge,
  HashableNodeId,
  Node,
  NodeId,
  nodeIdToHashable,
  V2Data,
} from 'dashboard/models/graph';
import { ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';

import {
  LightlyHydratedNode,
  ServerHydratedNode,
  ServerLightlyHydratedNode,
} from './graph-hydration';

export interface SnapshotNodeId {
  'datum-type-token': string;
  value: string;
}

export interface SnapshotNodeRendering {
  x?: number;
  y?: number;
  dimmed: boolean;
  excluded: boolean;
  featured: boolean;
  'non-fetched-edge-count': number;
}

export interface SnapshotNode {
  id: SnapshotNodeId;
  rendering: SnapshotNodeRendering;
  'hydration-values'?: ServerHydratedNode;
  'lightly-hydrated-values'?: ServerLightlyHydratedNode;
}

export interface SnapshotEdge {
  'node-id1': SnapshotNodeId;
  'node-id2': SnapshotNodeId;
  directionality: Directionality;
}

export interface GraphSnapshotAttributes {
  token: string;
  'created-at': string;
  'redacted-at': string | null;
  nodes: SnapshotNode[];
  edges: SnapshotEdge[];
  'seed-nodes': SnapshotNode[];
  'collapsed-groups': {
    members: SnapshotNodeId[];
    state: {
      excluded: boolean;
    };
    'relative-node-positions': {
      node_id: SnapshotNodeId;
      x: number;
      y: number;
    }[];
  }[];
}

export interface GraphSnapshotRelationships {
  graph: ApiRelationship<ApiModel.Graph>;
  environment: ApiRelationship<ApiModel.Environment>;
  'graph-query': ApiRelationship<ApiModel.GraphQuery>;
}

export type GraphSnapshot = ApiResource<
  GraphSnapshotAttributes,
  GraphSnapshotRelationships,
  ApiModel.GraphSnapshot
>;

export const snapshotNodeIdToGraphNodeId = (snapshotNodeId: SnapshotNodeId): NodeId => {
  return {
    token: snapshotNodeId['datum-type-token'],
    value: snapshotNodeId.value,
  };
};

const convertServerNodeToNode = (serverNode: SnapshotNode): Node => {
  return {
    id: snapshotNodeIdToGraphNodeId(serverNode.id),
    state: {
      visible: true,
      dimmed: serverNode.rendering.dimmed,
      excluded: serverNode.rendering.excluded,
      featured: serverNode.rendering.featured,
      nonfetchedEdgeCount: serverNode.rendering['non-fetched-edge-count'],
      new: false,
      loading: false,
      highlighted: false,
      selected: false,
    },
    hoverCards: new Set<HTMLDivElement>(),
  };
};

const convertServerEdgeToEdge = (serverEdge: SnapshotEdge): Edge => {
  return {
    id1: snapshotNodeIdToGraphNodeId(serverEdge['node-id1']),
    id2: snapshotNodeIdToGraphNodeId(serverEdge['node-id2']),
    directionality: Directionality.Bidirectional,
  };
};

export const convertSnaphotToGraphV2Data = (snapshot: GraphSnapshot): V2Data => {
  let nodes: Node[] = [];
  let edges: Edge[] = [];
  const nonfetchedEdgeCounts: Map<HashableNodeId, number> = new Map();

  if (snapshot?.attributes) {
    nodes = snapshot.attributes.nodes.map(convertServerNodeToNode);
    edges = snapshot.attributes.edges.map(convertServerEdgeToEdge);
    snapshot.attributes.nodes.map((node: SnapshotNode) => {
      nonfetchedEdgeCounts.set(
        nodeIdToHashable(snapshotNodeIdToGraphNodeId(node.id)),
        node.rendering['non-fetched-edge-count'],
      );
    });
  }

  return {
    nodes,
    edges,
    nonfetchedEdgeCounts,
    seedNodes: [],
  };
};

// NOTE: Modeling the inconsistency of the id name between FE and BE.
export interface PayloadNode {
  'node-id': SnapshotNodeId;
  rendering: SnapshotNodeRendering;
  'hydration-values'?: ServerHydratedNode;
  'lightly-hydrated-values'?: ServerLightlyHydratedNode;
}

export const convertToPayloadNode = (
  node: Node,
  g6Node: IBBox,
  lightlyHydratedNode: LightlyHydratedNode | null,
) => {
  return {
    'node-id': {
      'datum-type-token': node.id.token,
      value: node.id.value,
    },
    rendering: {
      x: g6Node.centerX,
      y: g6Node.centerY,
      dimmed: node.state.dimmed,
      excluded: node.state.excluded,
      featured: node.state.featured,
      'non-fetched-edge-count': node.state.nonfetchedEdgeCount,
    },
    'lightly-hydrated-values': lightlyHydratedNode?.['hydrationData'],
  };
};

export const convertToPayloadEdge = (edge: Edge): SnapshotEdge => {
  return {
    'node-id1': {
      'datum-type-token': edge.id1.token,
      value: edge.id1.value,
    },
    'node-id2': {
      'datum-type-token': edge.id2.token,
      value: edge.id2.value,
    },
    directionality: Directionality.Bidirectional,
  };
};
